import { Container, type Application } from 'pixi.js';

/**
 * Base Scene class. Each scene IS a Container — add children directly with
 * `this.addChild(...)`. Lifecycle hooks are optional; override as needed.
 *
 * The SceneManager calls onEnter(data) after adding the scene to the stage,
 * onExit() before removing it, and update(dtMs) every frame while active.
 */
export abstract class Scene extends Container {
  protected app!: Application;
  /** Used by scenes to enqueue cleanup that runs in onExit. */
  private cleanups: Array<() => void> = [];

  /** Called by the SceneManager after construction. Do NOT override. */
  _bind(app: Application): void {
    this.app = app;
    this.sortableChildren = true;
  }

  /** Override to populate the scene. Can be async if you need to await assets. */
  async onEnter(_data?: unknown): Promise<void> {
    // default no-op
  }

  /** Override to tear down. Always call super.onExit() if you override. */
  onExit(): void {
    this.cleanups.forEach((fn) => fn());
    this.cleanups.length = 0;
    this.removeAllListeners();
  }

  /** Override for per-frame logic. dtMs = milliseconds since last frame. */
  update(_dtMs: number): void {
    // default no-op
  }

  /** Register a cleanup callback to run on scene exit (listener removal, tween kill, etc.) */
  protected onCleanup(fn: () => void): void {
    this.cleanups.push(fn);
  }
}
